// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nochec

import './_map.scss';
import * as d3 from 'd3';
import { Tabulator, FormatModule, PageModule } from 'tabulator-tables';
import { replaceTimestamp } from '@/ts/utilities';
import { AV_LANGUAGES, DEFAULT_N_OF_PERIOD, DEFAULT_PERIOD, PERIOD_FILTERS, SPINNER_DURATION } from '@/ts/enums';
import { resolve } from '@/index';
import {SearchableSelect} from "@/ts/utils/custom-select-full";

/*
* This function create for init map component.
* */

export default function Map() {
    const $mapList = document.querySelectorAll('.map-list-component');

    if ($mapList.length) {
        $mapList.forEach(($item: any) => {
            const $mapWrapper = $item.querySelector('.map-list-wrapper') as HTMLDivElement;
            const $tooltip = $item.querySelector('.country-tooltip') as HTMLElement;
            const $tabsContainer = $item.querySelector('.tabs-container') as HTMLDivElement;
            const $tabs = $item.querySelectorAll('.tab');
            const $contentTabs = $item.querySelector('.tab-contents') as HTMLDivElement;
            const $tabsContent = $item.querySelectorAll('.tab-content');
            const $modal = $item.querySelector('.modal-full-screen') as HTMLDialogElement;
            const $tooltipModal = $modal.querySelector('.country-tooltip.modal') as HTMLElement;
            const $fullScreenBtn = $item.querySelector('.full-screen-btn') as HTMLElement;
            const $closeModalBtn = $modal.querySelector('.close-btn') as HTMLElement;
            const $numbers = $item.querySelectorAll('.number-value .number');
            const $lastUpdateDates = $item.querySelectorAll('.date.last-update');
            const $noResult = $item.querySelector('.no-result') as HTMLDivElement;
            const mapSelectedTab = $item.querySelector('.tab-content[id="map"] .map-container') as HTMLDivElement;
            let dataMap: any;
            let timestamp: any;
            let geoJson: object;
            const limitLength = 10;
            const dataRangeList = $item.querySelector('.data-range-list') as HTMLDivElement;
            const listItems = Array.from(dataRangeList.querySelectorAll('li'));
            //@ts-ignore;
            const htmlLang: any = (document.querySelector('html') as HTMLElement) ? document.querySelector('html').getAttribute('lang') : '';
            const currentLanguage: any = htmlLang && AV_LANGUAGES.find(lang => lang.includes(htmlLang)) ? AV_LANGUAGES.find(lang => lang.includes(htmlLang)) : 'en-GB';
            let $filtersSelect = $item.querySelectorAll('.select-container');
            let initialDefaultPeriod = DEFAULT_PERIOD;
            let initialDefaultNumberOfPeriod = DEFAULT_N_OF_PERIOD;

            Tabulator.registerModule([FormatModule, PageModule]);

            bindInitialSelect();

            try {
                const $geoMap = $item.querySelector('[data-map-geo]') as HTMLScriptElement;
                geoJson = $geoMap ? JSON.parse($geoMap.innerText) : null;

                if (geoJson) {
                    bindMap(initialDefaultPeriod, initialDefaultNumberOfPeriod);
                } else {
                    return null;
                }
            } catch (e) {
                $mapWrapper.classList.add('d-none');
                console.log('unable to retrieve map json');
            }

            replaceTimestamp();

            let projection = d3.geoAzimuthalEqualArea();
            let geoGenerator = d3.geoPath().projection(projection);

            let svg = d3.selectAll('svg');
            if (window.innerWidth > 768) {
                svg.attr('viewBox', '0 0 720 500').attr('preserveAspectRatio', 'xMidYMid');
            } else {
                svg.attr('viewBox', '0 0 520 464').attr('preserveAspectRatio', 'xMidYMid');
            }

            let zoom = d3.zoom().scaleExtent([0.5, 3]).on('zoom', handleZoom);

            let zoomModal = d3.zoom().scaleExtent([0.5, 3]).on('zoom', handleZoom);

            function init() {
                ui();
                handleTabs();
                initZoom();
                closeTooltip();
            }

            function handleTabs() {
                $tabs.forEach(($tab: any, index: number) => {
                    if (index === 0) {
                        $tab.classList.add('selected');

                        $tabsContent.forEach(($content: any) => {
                            const id = $content.getAttribute('id') as string;

                            if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                $content.classList.add('selected');
                                $contentTabs.classList.add(id);
                            } else {
                                $content.classList.remove('selected');
                                $contentTabs.classList.remove(id);
                            }
                        });
                    }

                    $tab.addEventListener('click', () => {
                        $tabs.forEach(($el: any) => {
                            $el.classList.remove('selected');
                        });
                        $tab.classList.add('selected');

                        $tabsContent.forEach(($content: any) => {
                            const id = $content.getAttribute('id') as string;
                            if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                $content.classList.add('selected');
                                $contentTabs.classList.add(id);
                            } else {
                                $content.classList.remove('selected');
                                $contentTabs.classList.remove(id);
                            }
                        });
                    });

                    $tab.addEventListener('keypress', (e: any) => {
                        // @ts-ignore
                        if (e.key === 'Enter') {
                            $tabs.forEach(($el: any) => {
                                $el.classList.remove('selected');
                            });
                            $tab.classList.add('selected');

                            $tabsContent.forEach(($content: any) => {
                                if ($tab.getAttribute('data-id') === $content.getAttribute('id')) {
                                    $content.classList.add('selected');
                                } else {
                                    $content.classList.remove('selected');
                                }
                            });
                        }
                    });
                });
            }

            function initMap(geoJson: any, data: any) {
                if (window.innerWidth > 768) {
                    projection.fitExtent(
                        [
                            [0, 0],
                            [720, 500],
                        ],
                        geoJson
                    );
                } else {
                    projection.fitExtent(
                        [
                            [0, 0],
                            [520, 480],
                        ],
                        geoJson
                    );
                }

                let u = d3.selectAll('.map-container g.map').selectAll('path').data(geoJson.features);

                u.enter()
                    .append('path')
                    .attr('d', geoGenerator)
                    .style('fill', function (d: any) {
                        const countryData = dataMap.find((item: any) => {
                            return item.code === d.properties.ISO2;
                        });

                        if (countryData) {
                            let countryColor: any;
                            let countryPercentage: any;
                            const totalCountries = dataMap.reduce((total: any, country: any) => total + country.total, 0);
                            if (totalCountries) {
                                countryPercentage = Number(((countryData.total / totalCountries) * 100).toFixed());
                            } else {
                                countryPercentage = 0;
                            }

                            const countriesValues = listItems.find((item: any) => {
                                const min = parseInt(item.getAttribute('data-min'));
                                const max = parseInt(item.getAttribute('data-max'));
                                return countryPercentage >= min && countryPercentage <= max;
                            });

                            if (countriesValues) {
                                countryColor = countriesValues.getAttribute('data-color');
                            }

                            return countryColor;
                        } else {
                            return 'rgba(0, 0, 0, 0.05)';
                        }
                    })

                    .on('click', function (d: any, i: any) {
                        const countryData = dataMap.find((item: any) => {
                            return item.code === i.properties.ISO2;
                        });

                        const modalMap = $modal.querySelector('.map-container') as any;

                        if (countryData) {
                            const totalCountries = dataMap.reduce((total: any, country: any) => total + country.total, 0);
                            let countriesWithPercentage: any;
                            if (totalCountries) {
                                countriesWithPercentage = dataMap.map((country: any) => ({
                                    ...country,
                                    percentage: (country.total / totalCountries) * 100,
                                }));
                            } else {
                                countriesWithPercentage = dataMap.map((country: any) => ({
                                    ...country,
                                    percentage: 0,
                                }));
                            }

                            const countryPercentage = countriesWithPercentage.find((country: any) => country.code === countryData.code);
                            const percentageValue = countryPercentage ? countryPercentage.percentage.toFixed() + '%' : '';
                            let tooltipDesktopReverted = false;
                            let tooltipModalIsOut = 0;
                            const modalScroll = $modal.scrollTop;
                            let topReverted = 0;
                            const legendWrapper = $item.querySelector('.legend-wrapper') ? $item.querySelector('.legend-wrapper').clientHeight : 0;

                            if ($tabsContainer.classList.contains('modal-open')) {
                                $tooltipModal.classList.remove('d-none');
                            } else {
                                $tooltip.classList.remove('d-none');
                            }

                            if (window.innerWidth > 768) {
                                if ($tabsContainer.classList.contains('modal-open')) {
                                    if (d.layerX > modalMap.clientWidth / 2) {
                                        $tooltipModal.classList.add('reverted');
                                        tooltipDesktopReverted = true;

                                        if (d.layerX > modalMap.clientWidth - 30) {
                                            tooltipModalIsOut = 30;
                                        }
                                    } else {
                                        $tooltipModal.classList.remove('reverted');
                                        tooltipDesktopReverted = false;

                                        if (d.layerX < 30) {
                                            tooltipModalIsOut = 30;
                                        }
                                    }

                                    if (d.layerY < modalMap.clientHeight / 2) {
                                        $tooltipModal.classList.add('up');
                                        topReverted = 250;
                                    } else {
                                        $tooltipModal.classList.remove('up');
                                    }
                                } else {
                                    if (d.layerX > mapSelectedTab.clientWidth / 2) {
                                        $tooltip.classList.add('reverted');
                                    } else {
                                        $tooltip.classList.remove('reverted');
                                    }
                                }
                            } else {
                                if (d.layerX > mapSelectedTab.clientWidth / 2) {
                                    $tooltip.classList.add('reverted');
                                    $tooltipModal.classList.add('reverted');

                                    if (d.layerX > modalMap.clientWidth - 30) {
                                        tooltipModalIsOut = 30;
                                    }
                                } else {
                                    $tooltipModal.classList.remove('reverted');
                                    $tooltip.classList.remove('reverted');

                                    if (d.layerX < 30) {
                                        tooltipModalIsOut = 30;
                                    }
                                }

                                if ($tabsContainer.classList.contains('modal-open')) {
                                    if (d.layerY < modalMap.clientHeight / 2) {
                                        $tooltipModal.classList.add('up');
                                        topReverted = 200;
                                    } else {
                                        topReverted = 50;
                                        $tooltipModal.classList.remove('up');
                                    }
                                }
                            }

                            d3.select('.country-tooltip')
                                .style('top', window.innerWidth > 768 ? d.layerY - 110 + legendWrapper + 'px' : d.layerY - 80 + legendWrapper + 'px')
                                .style('left', function () {
                                    let newLeftValue = d.layerX > mapSelectedTab.clientWidth / 2 ? d.layerX - 200 : d.layerX - 30;
                                    if (window.innerWidth > 768) {
                                        return newLeftValue + 'px';
                                    } else {
                                        if (d.layerX > mapSelectedTab.clientWidth / 2) {
                                            return newLeftValue + 75 + 'px';
                                        } else {
                                            return newLeftValue + 'px';
                                        }
                                    }
                                });

                            d3.select('.country-tooltip.modal')
                                .style('top', window.innerWidth > 768 ? d.clientY - 250 + modalScroll + topReverted + 'px' : d.clientY - 200 + modalScroll + topReverted + 'px')
                                .style(
                                    'left',
                                    window.innerWidth > 768
                                        ? tooltipDesktopReverted
                                            ? d.clientX - 250 - tooltipModalIsOut + 'px'
                                            : d.clientX - 80 + tooltipModalIsOut + 'px'
                                        : d.layerX > mapSelectedTab.clientWidth / 2
                                          ? d.layerX - tooltipModalIsOut - 125 + 'px'
                                          : d.clientX - 50 + tooltipModalIsOut + 'px'
                                );

                            window.addEventListener('resize', function () {
                                if (window.innerWidth < 768) {
                                    $tooltipModal.classList.add('reverted');
                                    d3.select('.country-tooltip')
                                        .style('top', window.innerWidth > 768 ? d.layerY - 110 + 'px' : d.layerY - 50 + 'px')
                                        .style('left', function () {
                                            let newLeftValue = d.layerX > mapSelectedTab.clientWidth / 2 ? d.layerX - 200 : d.layerX - 30;
                                            return newLeftValue + 'px';
                                        });

                                    d3.select('.country-tooltip.modal')
                                        .style('top', d.clientY - 230 + 'px')
                                        .style('left', function () {
                                            let newLeftValue = d.layerX > window.innerWidth / 2 ? d.layerX - 200 : d.clientX - 70;
                                            return newLeftValue + 'px';
                                        });
                                } else {
                                    $tooltipModal.classList.remove('reverted');
                                    d3.select('.country-tooltip.modal')
                                        .style('top', d.clientY - 250 + 'px')
                                        .style('left', d.clientX - 70 + 'px');
                                }
                            });

                            d3.selectAll('.country-tooltip .country-name').text(function () {
                                return Drupal.t(countryData.label);
                            });

                            d3.selectAll('.country-tooltip .total .number').text(function () {
                                return countryData.total.toLocaleString().replace(/,/g, '.');
                            });

                            d3.selectAll('.country-tooltip .total .percentage').text(function () {
                                return percentageValue;
                            });

                            d3.selectAll('.map-container g.map path').style('stroke', '#FFF').style('stroke-width', '0.2px');

                            d3.select(this).style('stroke', '#1A1A1A').style('stroke-width', '1px');
                        }
                    })
                    .each(function (d: any) {
                        const countryData = dataMap.find((item: any) => {
                            return item.code === d.properties.ISO2;
                        });
                        if (countryData) {
                            d3.select(this).style('cursor', 'pointer');
                        }
                    });

                u.style('fill', function (d: any) {
                    const countryData = dataMap.find((item: any) => {
                        return item.code === d.properties.ISO2;
                    });

                    if (countryData) {
                        let countryColor: any;
                        let countryPercentage: any;
                        const totalCountries = dataMap.reduce((total: any, country: any) => total + country.total, 0);
                        if (totalCountries) {
                            countryPercentage = Number(((countryData.total / totalCountries) * 100).toFixed());
                        } else {
                            countryPercentage = 0;
                        }

                        const countriesValues = listItems.find((item: any) => {
                            const min = parseInt(item.getAttribute('data-min'));
                            const max = parseInt(item.getAttribute('data-max'));
                            return countryPercentage >= min && countryPercentage <= max;
                        });

                        if (countriesValues) {
                            countryColor = countriesValues.getAttribute('data-color');
                        }

                        return countryColor;
                    } else {
                        return 'rgba(0, 0, 0, 0.05)';
                    }
                });

                function nFormatter(num: any) {
                    const span = $item.querySelector('.number-value .label-num') as HTMLElement;
                    const $spanNumberValue = $item.querySelectorAll('.value-num');
                    let splitText: any;

                    $spanNumberValue.forEach(($el: any) => {
                        const text = $el.innerText;
                        splitText = text.split(',');
                    });

                    if (num >= 1000000000) {
                        return (num / 1000000000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitText[2] + ' ' + span.innerText.trim() + '</span>';
                    }
                    if (num >= 1000000) {
                        return (num / 1000000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitText[1] + ' ' + span.innerText.trim() + '</span>';
                    }
                    if (num >= 1000) {
                        return (num / 1000).toFixed(3).replace(/\.0$/, '').replace('.', ',') + '<span class="value">' + splitText[0] + ' ' + span.innerText.trim() + '</span>';
                    }
                    return num.toString().replace('.', ',') + '<span class="value">' + span.innerText.trim() + '</span>';
                }

                let total = 0;
                data.forEach((item: any) => {
                    total += item.total;
                });

                if ($numbers) {
                    $numbers.forEach(($number: any) => {
                        if (!$number.innerHTML.includes(total.toString())) {
                            $number.innerHTML = nFormatter(total);
                        }
                    });
                }
            }

            function ui() {
                $fullScreenBtn.addEventListener('click', () => {
                    openFullScreenModal();
                });

                $fullScreenBtn.addEventListener('keypress', e => {
                    if (e.key === 'Enter') {
                        openFullScreenModal();
                        $closeModalBtn.focus();
                    }
                });

                $closeModalBtn.addEventListener('click', () => {
                    closeFullScreenModal();
                });

                $closeModalBtn.addEventListener('keypress', e => {
                    if (e.key === 'Enter') {
                        $fullScreenBtn.focus();
                        closeFullScreenModal();
                    }
                });

                document.addEventListener('click', event => {
                    if ($modal && !(event.target as HTMLElement).contains($modal)) return;
                    closeFullScreenModal();
                });
            }

            function closeTooltip() {
                const $icnClose = $item.querySelectorAll('.country-tooltip .icn-close');

                $icnClose.forEach(($icn: any) => {
                    $icn.addEventListener('click', () => {
                        $tooltipModal.classList.add('d-none');
                        $tooltip.classList.add('d-none');

                        d3.selectAll('.map-container g.map path').style('stroke', '#FFF').style('stroke-width', '0.2px');
                    });
                });
            }

            function closeFullScreenModal() {
                $tooltipModal.classList.add('d-none');
                $tooltip.classList.add('d-none');
                $modal.close();
                $tabsContainer.classList.remove('modal-open');
            }

            function openFullScreenModal() {
                const $allSelect = $modal.querySelectorAll('.custom-select');
                $modal.showModal();
                $tabsContainer.classList.add('modal-open');

                $allSelect.forEach(($select: any) => {
                    const $openerBtn = $select.querySelector('.custom-select__opener') as HTMLElement;
                    const $panel = $select.querySelector('.custom-select__panel') as HTMLElement;

                    $openerBtn.addEventListener('click', e => {
                        const $options = $select.querySelectorAll('.custom-select__option');
                        let optionsHeight = 0;
                        const modalHeight = $modal.clientHeight;
                        const selectPosition = e.clientY;

                        $options.forEach(($op: any) => {
                            optionsHeight = optionsHeight + $op.clientHeight;
                        });

                        if (modalHeight - optionsHeight < selectPosition) {
                            $panel.classList.add('top');
                        } else {
                            $panel.classList.remove('top');
                        }
                    });
                });

                initMap(geoJson, dataMap);
            }

            function initTable(data: any) {
                const $tableLabel = $item.querySelector('.table-label') as HTMLDivElement;
                const orderedData = data.sort((a: any, b: any) => b.total - a.total);
                let totalCountries: any;
                totalCountries = orderedData.reduce((total: any, country: any) => total + country.total, 0);
                const dataTable = orderedData.map((country: any) => {
                    return {
                        country: Drupal.t(country.label),
                        total_value: country.total.toLocaleString().replace(/,/g, '.') + ' ' + ($tableLabel && $tableLabel.innerText),
                        country_percentage: (country.total / totalCountries) * 100,
                    };
                });

                let temp = false;
                let labelFind = false;
                const columns = Object.keys(dataTable[0])
                    .map(key => {
                        if (!labelFind || !temp) {
                            if (key === 'country') {
                                labelFind = true;
                            } else {
                                temp = true;
                            }
                            return {
                                title: key,
                                field: key === 'country_percentage' || key === 'total_value' ? 'item' : key,
                                formatter:
                                    key === 'country_percentage' || key === 'total_value'
                                        ? function (cell: any) {
                                              let data = cell.getData();
                                              let combinedValueText = data.total_value + '<div class="progress--wrapper"><progress value="' + data.country_percentage + '" max="100"></progress></div>';
                                              return '<div class="value-wrapper">' + combinedValueText + '</div>';
                                          }
                                        : '',
                                widthGrow: key === 'country' ? 2 : 1,
                            };
                        }
                        return null;
                    })
                    .filter(el => !!el);

                const $tables = $item.querySelectorAll('.table');
                if ($tables.length) {
                    $tables.forEach(($table: any) => {
                        const tabulator = new Tabulator($table, {
                            data: dataTable,
                            columns: columns,
                            layout: 'fitColumns',
                            headerVisible: false,
                            selectable: false,
                            pagination: dataTable.length > limitLength,
                            paginationSize: limitLength,
                            paginationButtonCount: 3,
                        });

                        tabulator.on('dataLoaded', function () {
                            const paginator = $table.querySelector('.tabulator-paginator') as HTMLElement;
                            const btnPrev = $table.querySelector('button[data-page="prev"]') as HTMLElement;
                            const btnNext = $table.querySelector('button[data-page="next"]') as HTMLElement;
                            if (btnPrev) {
                                btnPrev.innerHTML = '';
                            }
                            if (btnNext) {
                                btnNext.innerHTML = '';
                            }
                            paginator && paginator.setAttribute('aria-label', 'navigazione del paginatore');
                            paginator && paginator.setAttribute('role', 'navigation');
                        });
                    });
                }
            }

            function initZoom() {
                d3.select('svg').call(zoom);

                d3.select('.modal svg').call(zoomModal);
            }

            function handleZoom(event: any) {
                $tooltipModal.classList.add('d-none');
                $tooltip.classList.add('d-none');
                closeTooltip();
                if ($modal && $tabsContainer.classList.contains('modal-open')) {
                    d3.select('.modal svg g').attr('transform', event.transform);
                } else {
                    d3.select('svg g').attr('transform', event.transform);
                }
            }

            function zoomIn() {
                if ($modal && $tabsContainer.classList.contains('modal-open')) {
                    d3.select('.modal svg').transition().call(zoomModal.scaleBy, 1.5);
                } else {
                    d3.select('svg').transition().call(zoom.scaleBy, 1.5);
                }
            }

            function zoomOut() {
                if ($modal && $tabsContainer.classList.contains('modal-open')) {
                    d3.select('.modal svg').transition().call(zoomModal.scaleBy, 0.75);
                } else {
                    d3.select('svg').transition().call(zoom.scaleBy, 0.75);
                }
            }

            function bindMap(period: any, numberOfPeriod: any) {
                $item.classList.add('has-spinner');
                try {
                    resolve
                        .run('getGeographicVisits', period, numberOfPeriod)
                        .then((res: any) => {
                            if (!res.data) {
                                throw new Error();
                            } else {
                                dataMap = res.data.countries ? [...res.data.countries] : [];
                                timestamp = res.timestamp;

                                dataMap = dataMap && dataMap.length && dataMap.filter((dataEl: any) => !isNaN(dataEl.total) && dataEl.total !== null);

                                const date = new Date(timestamp);
                                const formattedDate = date.toLocaleDateString(currentLanguage, {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                });

                                if ($lastUpdateDates) {
                                    $lastUpdateDates.forEach(($date: any) => {
                                        $date.textContent = formattedDate;
                                    });
                                }

                                setTimeout(() => {
                                    initMap(geoJson, dataMap);
                                    initTable(dataMap);
                                    $noResult?.classList.add('d-none');
                                    $item.classList.remove('has-spinner');
                                }, SPINNER_DURATION);
                            }
                        })
                        .catch(e => {
                            //console.log(e);
                            setTimeout(() => {
                                $noResult?.classList.remove('d-none');
                                $item.classList.remove('has-spinner');
                                const $mapContent = $item.querySelector('.tab-content#map') as HTMLDivElement;
                                const $smallDevices = $item.querySelector('.small-devices') as HTMLDivElement;
                                const $title = $item.querySelector('.title.tertiary') as HTMLElement;
                                const $number = $item.querySelector('.number-value') as HTMLDivElement;
                                const $lastUpdateDate = $item.querySelector('.info-details .last-update') as HTMLDivElement;
                                $title?.classList.add('d-none');
                                $number?.classList.add('d-none');
                                $mapContent?.classList.add('d-none');
                                $smallDevices.classList.add('d-none');
                                ($lastUpdateDate?.parentElement as HTMLDivElement)?.classList.add('d-none');
                            }, SPINNER_DURATION);
                        });
                } catch (e) {
                    $item.classList.remove('has-spinner');
                    console.log(e);
                }
            }

            function bindSelects() {
                $filtersSelect.forEach(($select: any) => {
                    const $options = $select.querySelectorAll('.option-div');
                    const selectType = $select.getAttribute('data-select-type');

                    $options.forEach(($option: any) => {
                        $option.onclick = () => {
                            if (selectType === 'period') {
                                let numberOfPeriod = $option.getAttribute('option-id');
                                numberOfPeriod = numberOfPeriod && numberOfPeriod.split('-') ? numberOfPeriod.split('-')[0] : null;
                                const period = $option.getAttribute('data-type');
                                initialDefaultPeriod = period ? period : initialDefaultPeriod;
                                initialDefaultNumberOfPeriod = numberOfPeriod ? numberOfPeriod : initialDefaultNumberOfPeriod;

                                updateSelects(period, numberOfPeriod, selectType);
                            }

                            bindMap(initialDefaultPeriod, initialDefaultNumberOfPeriod);
                        };
                    });
                });
            }

            function updateSelects(period: any, NPeriod: any, type: any) {
                $filtersSelect.forEach(($select: any) => {
                    if ($select.getAttribute('data-select-type') === type) {
                        const $nativeSelect = $select.querySelector('select');
                        const $options = $nativeSelect.querySelectorAll('option');

                        $options.forEach(($option: any) => {
                            let optionValue = $option.value?.split('-') ? $option.value?.split('-') : $option.value?.split('-')[0];
                            $option.selected = optionValue === NPeriod && $option.getAttribute('data-type') === period;
                            $nativeSelect.value = NPeriod + '-' + period;
                        });

                        SearchableSelect.update($select);

                        $filtersSelect = $item.querySelectorAll('.select-container');

                        bindSelects();
                    }
                });
            }

            function bindInitialSelect() {
                const filters = PERIOD_FILTERS || [];
                $filtersSelect.forEach(($select: any) => {
                    if ($select.getAttribute('data-select-type') && $select.getAttribute('data-select-type') === 'period') {
                        const $nativeSelect = $select.querySelector('select');
                        filters.forEach((filterEL: any, index: number) => {
                            const $option = document.createElement('option') as HTMLOptionElement;
                            $option.setAttribute('value', filterEL.n_period + '-' + filterEL.period);
                            $option.setAttribute('data-type', filterEL.period);
                            $option.innerText = Drupal.t(filterEL.label);
                            if(index === 0){
                                $option.selected = true;
                            }
                            $nativeSelect.appendChild($option);
                        });

                        SearchableSelect.init($nativeSelect, 'map-select');
                    }
                });

                $filtersSelect = $item.querySelectorAll('.select-container');

                bindSelects();
            }

            const $zoomInBtn = $item.querySelectorAll('.zoom-in-btn');
            $zoomInBtn.forEach(($btn: any) => {
                $btn.addEventListener('click', () => zoomIn());

                $btn.addEventListener('keypress', (e: any) => {
                    if (e.key === 'Enter') {
                        zoomIn();
                    }
                });
            });

            const $zoomOutBtn = $item.querySelectorAll('.zoom-out-btn');
            $zoomOutBtn.forEach(($btn: any) => {
                $btn.addEventListener('click', () => zoomOut());

                $btn.addEventListener('keypress', (e: any) => {
                    if (e.key === 'Enter') {
                        zoomOut();
                    }
                });
            });

            init();

            window.addEventListener('orientationchange', () => {
                if (window.innerHeight <= 649 && window.innerHeight < window.innerWidth) {
                    closeFullScreenModal();
                }
            });

            return true;
        });
    }
}
